import { useModel } from 'umi';
import React, { useState, useEffect } from 'react';
import PageMain from '@/components/page-main';
import CommentDetail from './components/commentDetail';
import {
  Layout,
  Card,
  List,
  Button,
  Input,
  Space,
  message,
  Divider,
  Row,
  Col,
  Collapse,
  Tooltip,
  Table,
  Select,
  Switch,
  Tabs,
} from 'antd';
import { CheckOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  startAutoReply,
  stopAutoReply,
  addReply,
  listReplies,
  deleteReply,
  muteUser,
  unmuteUser,
  mutedUserGet,
  listAutoReplyTasks,
  createAutoReplyTask,
  getSxtProcesses,
  listNotes,
  activateAutoReplyTask,
  deactivateAutoReplyTask,
  getSchedules,
} from '@/services/xiaohongshu';

const { Content } = Layout;
const { TextArea } = Input;
const { Panel } = Collapse;
const { Option } = Select;

interface UserListItem {
  userId: string;
  nickname?: string;
}

interface TaskItem {
  id?: string;
  noteId: string;
  reply: string;
  replyWith: 'comment' | 'sxt';
  listenerId?: string;
  isActive: boolean;
  isEditing?: boolean;
}

interface NoteItem {
  id: string;
  title: string;
}

interface SxtProcess {
  id: string;
  name: string;
}

const XhsComment: React.FC = () => {
  const { initialState } = useModel('@@initialState');
  const [name, setName] = useState<string>(
    initialState?.currentUser?.name || '',
  );

  // Tab切换状态
  const [activeTab, setActiveTab] = useState<string>('settings');

  // State for Blacklist/Greylist
  const [commonUserIdInput, setCommonUserIdInput] = useState<string>('');
  const [commonNicknameInput, setCommonNicknameInput] = useState<string>('');
  const [blacklist, setBlacklist] = useState<UserListItem[]>([]);
  const [greylist, setGreylist] = useState<UserListItem[]>([]);

  // State for Tasks
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [notes, setNotes] = useState<NoteItem[]>([]);
  const [sxtProcesses, setSxtProcesses] = useState<SxtProcess[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // 添加状态管理
  const [autoReplyRunning, setAutoReplyRunning] = useState(false);

  useEffect(() => {
    if (name) {
      fetchTasks();
      fetchNotes();
      fetchSxtProcesses();
      fetchMutedUsers();
      checkAutoReplyStatus(); // 新增：检查自动回复状态
    }
  }, [name]);

  // Fetch functions
  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await listAutoReplyTasks({ name });
      if (response?.resp?.data) {
        setTasks(
          response.resp.data.map((task: any) => ({
            id: task.id,
            noteId: task.noteId,
            reply: task.reply,
            replyWith: task.replyWith || 'comment',
            listenerId: task.listenerId,
            isActive: task.isActive,
          })),
        );
      }
    } catch (error) {
      message.error('获取任务列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await listNotes({ name, page: 1, pageSize: 100 });
      if (response?.resp?.data) {
        setNotes(
          response.resp.data.map((note: any) => ({
            id: note.id,
            title: note.title || `笔记 #${note.id}`,
          })),
        );
      }
    } catch (error) {
      message.error('获取笔记列表失败');
    }
  };

  const fetchSxtProcesses = async () => {
    try {
      const response = await getSxtProcesses({});
      if (response?.resp?.data) {
        setSxtProcesses(
          response.resp.data.map((process: any) => ({
            id: process,
            name: process.name || `流程 #${process}`,
          })),
        );
      }
    } catch (error) {
      message.error('获取流程列表失败');
    }
  };

  const fetchMutedUsers = async () => {
    try {
      const [blackResponse, greyResponse] = await Promise.all([
        mutedUserGet({ name, type: 'black' }),
        mutedUserGet({ name, type: 'grey' }),
      ]);

      if (blackResponse?.resp?.data) {
        setBlacklist(
          blackResponse.resp.data.map((item: any) => ({
            userId: item.user_id,
            nickname: item.nickname || undefined,
          })),
        );
      }

      if (greyResponse?.resp?.data) {
        setGreylist(
          greyResponse.resp.data.map((item: any) => ({
            userId: item.user_id,
            nickname: item.nickname || undefined,
          })),
        );
      }
    } catch (error) {
      message.error('获取用户列表失败');
    }
  };

  // Task operations
  const handleAddNewTask = () => {
    const newTask: TaskItem = {
      noteId: '',
      reply: '',
      replyWith: 'comment',
      listenerId: '',
      isActive: false,
      isEditing: true,
    };
    setTasks([...tasks, newTask]);
  };

  const handleSaveTask = async (index: number, task: TaskItem) => {
    if (!task.noteId || !task.reply) {
      message.error('笔记和话术不能为空');
      return;
    }

    if (task.replyWith === 'sxt' && !task.listenerId) {
      message.error('选择私信时流程不能为空');
      return;
    }

    try {
      // 这里调用创建或更新任务的API
      const response = await createAutoReplyTask({
        name,
        reply: task.reply,
        noteId: parseInt(task.noteId, 10),
        replyWith: task.replyWith,
        listenerId: task.listenerId || '',
      });

      if (response?.resp?.data) {
        const savedTask = response.resp.data;
        const updatedTasks = [...tasks];
        updatedTasks[index] = {
          ...task,
          id: savedTask.id,
          isEditing: false,
        };
        setTasks(updatedTasks);
      } else {
        message.error('任务保存失败');
      }
    } catch (error) {
      message.error('任务保存失败');
    }
  };

  const handleCancelEdit = (index: number) => {
    const updatedTasks = [...tasks];
    if (!updatedTasks[index].id) {
      // 新建的任务，直接删除
      updatedTasks.splice(index, 1);
    } else {
      // 编辑的任务，取消编辑状态
      updatedTasks[index].isEditing = false;
    }
    setTasks(updatedTasks);
  };

  const handleToggleTask = async (index: number, isActive: boolean) => {
    try {
      const task = tasks[index];
      if (isActive) {
        await activateAutoReplyTask({ name, taskId: Number(task.id) });
      } else {
        await deactivateAutoReplyTask({ name, taskId: Number(task.id) });
      }

      const updatedTasks = [...tasks];
      updatedTasks[index].isActive = isActive;
      setTasks(updatedTasks);
      message.success(isActive ? '任务已开启' : '任务已停止');
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleUpdateTask = (
    index: number,
    field: keyof TaskItem,
    value: any,
  ) => {
    const updatedTasks = [...tasks];
    updatedTasks[index] = { ...updatedTasks[index], [field]: value };
    setTasks(updatedTasks);
  };

  // Blacklist/Greylist handlers (keep existing)
  const handleAddToBlacklist = async () => {
    if (!commonUserIdInput.trim()) {
      message.error('用户ID不能为空');
      return;
    }
    const newItem: UserListItem = {
      userId: commonUserIdInput.trim(),
      nickname: commonNicknameInput.trim() || undefined,
    };
    await muteUser({
      name: name,
      user_id: newItem.userId,
      type: 'black',
      nickname: newItem.nickname,
    });
    setBlacklist((prev) => {
      if (prev.find((item) => item.userId === newItem.userId)) {
        message.warn('该用户已在黑名单中');
        return prev;
      }
      return [...prev, newItem];
    });
    message.success(`用户 ${newItem.userId} 已添加到黑名单`);
    setCommonUserIdInput('');
    setCommonNicknameInput('');
  };

  const handleAddToGreylist = async () => {
    if (!commonUserIdInput.trim()) {
      message.error('用户ID不能为空');
      return;
    }
    const newItem: UserListItem = {
      userId: commonUserIdInput.trim(),
      nickname: commonNicknameInput.trim() || undefined,
    };
    await muteUser({
      name: name,
      user_id: newItem.userId,
      type: 'grey',
      nickname: newItem.nickname,
    });
    setGreylist((prev) => {
      if (prev.find((item) => item.userId === newItem.userId)) {
        message.warn('该用户已在灰名单中');
        return prev;
      }
      return [...prev, newItem];
    });
    message.success(`用户 ${newItem.userId} 已添加到灰名单`);
    setCommonUserIdInput('');
    setCommonNicknameInput('');
  };

  const handleDeleteFromBlacklist = async (userIdToDelete: string) => {
    await unmuteUser({
      name: name,
      user_id: userIdToDelete,
      type: 'black',
    });
    setBlacklist((prev) =>
      prev.filter((item) => item.userId !== userIdToDelete),
    );
    message.success(`用户 ${userIdToDelete} 已从黑名单删除`);
  };

  const handleDeleteFromGreylist = async (userIdToDelete: string) => {
    await unmuteUser({
      name: name,
      user_id: userIdToDelete,
      type: 'grey',
    });
    setGreylist((prev) =>
      prev.filter((item) => item.userId !== userIdToDelete),
    );
    message.success(`用户 ${userIdToDelete} 已从灰名单删除`);
  };

  // Table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (id: string) => id || '-',
    },
    {
      title: '笔记',
      dataIndex: 'noteId',
      key: 'noteId',
      width: 300, // 从200增加到300
      render: (noteId: string, record: TaskItem, index: number) => {
        if (record.isEditing) {
          return (
            <Select
              value={noteId}
              onChange={(value) => handleUpdateTask(index, 'noteId', value)}
              style={{ width: '100%' }}
              placeholder="选择笔记"
            >
              {notes.map((note) => (
                <Option key={note.id} value={note.id}>
                  {note.title}
                </Option>
              ))}
            </Select>
          );
        }
        const note = notes.find((n) => n.id === noteId);
        return note ? note.title : noteId;
      },
    },
    {
      title: '话术',
      dataIndex: 'reply',
      key: 'reply',
      // 移除固定宽度，让它填满剩余空间
      render: (reply: string, record: TaskItem, index: number) => {
        if (record.isEditing) {
          return (
            <TextArea
              value={reply}
              onChange={(e) => handleUpdateTask(index, 'reply', e.target.value)}
              placeholder="输入回复话术"
              rows={2}
            />
          );
        }
        return (
          <div style={{ wordBreak: 'break-word' }}>{reply}</div> // 移除maxWidth限制
        );
      },
    },
    {
      title: '渠道',
      dataIndex: 'replyWith',
      key: 'replyWith',
      width: 120,
      render: (replyWith: string, record: TaskItem, index: number) => {
        if (record.isEditing) {
          return (
            <Select
              value={replyWith}
              onChange={(value) => handleUpdateTask(index, 'replyWith', value)}
              style={{ width: '100%' }}
            >
              <Option value="comment">评论</Option>
              <Option value="sxt">私信</Option>
            </Select>
          );
        }
        return replyWith === 'comment' ? '评论' : '私信';
      },
    },
    {
      title: '流程',
      dataIndex: 'listenerId',
      key: 'listenerId',
      width: 200, // 从150增加到200
      render: (listenerId: string, record: TaskItem, index: number) => {
        if (record.isEditing) {
          return (
            <Select
              value={listenerId}
              onChange={(value) => handleUpdateTask(index, 'listenerId', value)}
              style={{ width: '100%' }}
              placeholder="选择流程"
              disabled={record.replyWith !== 'sxt'}
            >
              {sxtProcesses.map((process) => (
                <Option key={process.id} value={process.id}>
                  {process.name}
                </Option>
              ))}
            </Select>
          );
        }
        if (record.replyWith !== 'sxt') return '-';
        const process = sxtProcesses.find((p) => p.id === listenerId);
        return process ? process.name : listenerId;
      },
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <span style={{ color: isActive ? '#52c41a' : '#ff4d4f' }}>
          {isActive ? '运行中' : '已停止'}
        </span>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: TaskItem, index: number) => {
        if (record.isEditing) {
          return (
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveTask(index, record)}
              >
                保存
              </Button>
              <Button size="small" onClick={() => handleCancelEdit(index)}>
                取消
              </Button>
            </Space>
          );
        }

        return (
          <Space>
            <Switch
              checked={record.isActive}
              onChange={(checked) => handleToggleTask(index, checked)}
              checkedChildren="激活"
              unCheckedChildren="禁用"
            />
          </Space>
        );
      },
    },
  ];

  // 新增：检查自动回复状态的函数
  const checkAutoReplyStatus = async () => {
    try {
      const response = await getSchedules({});
      if (response?.resp?.data && Array.isArray(response.resp.data)) {
        const runningSchedules: string[] = response.resp.data;
        const isRunning = runningSchedules.includes(name);
        setAutoReplyRunning(isRunning);
      }
    } catch (error) {
      console.error('检查自动回复状态失败:', error);
      // 如果获取状态失败，默认为未运行状态
      setAutoReplyRunning(false);
    }
  };

  // 修改处理函数
  const handleToggleAutoReply = async () => {
    try {
      if (autoReplyRunning) {
        // 停止自动回复
        const response = await stopAutoReply({ name });
        if (response?.resp?.code !== 0) {
          message.error('停止自动回复失败');
          return;
        }
        message.success('自动回复已停止');
        setAutoReplyRunning(false);
      } else {
        // 启动自动回复
        const response = await startAutoReply({ name });
        if (response?.resp?.code !== 0) {
          message.error('启动自动回复失败');
          return;
        }
        message.success('自动回复已启动');
        setAutoReplyRunning(true);
      }

      // 操作完成后重新检查状态，确保UI显示正确
      await checkAutoReplyStatus();
    } catch (error) {
      message.error('操作失败');
      // 出错后也检查一下状态
      await checkAutoReplyStatus();
    }
  };

  return (
    <PageMain>
      <Content
        style={{
          margin: '24px 16px',
          padding: 24,
          background: '#fff',
          minHeight: '360px',
        }}
      >
        <Card
          title={
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>自动回复</span>
              <Button
                type="primary"
                onClick={handleToggleAutoReply}
                style={{
                  backgroundColor: autoReplyRunning ? '#ff4d4f' : '#52c41a',
                  borderColor: autoReplyRunning ? '#ff4d4f' : '#52c41a',
                }}
              >
                {autoReplyRunning ? '停止' : '启动'}
              </Button>
            </div>
          }
          style={{ width: '100%' }}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'details',
                label: '评论详情',
                children: (
                  <div>
                    <CommentDetail name={name} sxtProcesses={sxtProcesses} />
                  </div>
                ),
              },
              {
                key: 'settings',
                label: '回复设置',
                children: (
                  <div>
                    {/* Blacklist/Greylist Section */}
                    <Divider orientation="left" orientationMargin={0}>
                      灰/黑名单
                    </Divider>
                    <Space
                      direction="vertical"
                      style={{ width: '100%', marginBottom: '24px' }}
                    >
                      <Space>
                        <Input
                          placeholder="用户ID (必填)"
                          value={commonUserIdInput}
                          onChange={(e) => setCommonUserIdInput(e.target.value)}
                          style={{ width: 200 }}
                        />
                        <Input
                          placeholder="昵称 (可选)"
                          value={commonNicknameInput}
                          onChange={(e) =>
                            setCommonNicknameInput(e.target.value)
                          }
                          style={{ width: 200 }}
                        />
                        <Button onClick={handleAddToBlacklist}>
                          添加到黑名单
                        </Button>
                        <Button onClick={handleAddToGreylist}>
                          添加到灰名单
                        </Button>
                      </Space>

                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <Collapse
                            defaultActiveKey={['blacklist-panel']}
                            ghost
                          >
                            <Panel
                              header={`黑名单列表 (${blacklist.length})`}
                              key="blacklist-panel"
                            >
                              <List
                                size="small"
                                bordered
                                dataSource={blacklist}
                                locale={{ emptyText: '暂无数据' }}
                                renderItem={(item) => (
                                  <List.Item
                                    actions={[
                                      <DeleteOutlined
                                        key={`bl-delete-${item.userId}`}
                                        onClick={() =>
                                          handleDeleteFromBlacklist(item.userId)
                                        }
                                      />,
                                    ]}
                                  >
                                    <Tooltip title={item.nickname || '无昵称'}>
                                      <span>ID: {item.userId}</span>
                                    </Tooltip>
                                  </List.Item>
                                )}
                              />
                            </Panel>
                          </Collapse>
                        </Col>
                        <Col xs={24} md={12}>
                          <Collapse defaultActiveKey={['greylist-panel']} ghost>
                            <Panel
                              header={`灰名单列表 (${greylist.length})`}
                              key="greylist-panel"
                            >
                              <List
                                size="small"
                                bordered
                                dataSource={greylist}
                                locale={{ emptyText: '暂无数据' }}
                                renderItem={(item) => (
                                  <List.Item
                                    actions={[
                                      <DeleteOutlined
                                        key={`wl-delete-${item.userId}`}
                                        onClick={() =>
                                          handleDeleteFromGreylist(item.userId)
                                        }
                                      />,
                                    ]}
                                  >
                                    <Tooltip title={item.nickname || '无昵称'}>
                                      <span>ID: {item.userId}</span>
                                    </Tooltip>
                                  </List.Item>
                                )}
                              />
                            </Panel>
                          </Collapse>
                        </Col>
                      </Row>
                    </Space>

                    {/* Tasks Section */}
                    <Divider orientation="left" orientationMargin={0}>
                      任务列表
                    </Divider>
                    <div style={{ marginBottom: 16 }}>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddNewTask}
                      >
                        新建任务
                      </Button>
                    </div>
                    <Table
                      columns={columns}
                      dataSource={tasks}
                      loading={loading}
                      rowKey={(record, index) => record.id || `new-${index}`}
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: false,
                        showQuickJumper: true,
                      }}
                      scroll={{ x: 1000 }}
                    />
                  </div>
                ),
              },
            ]}
          />
        </Card>
      </Content>
    </PageMain>
  );
};

export default XhsComment;
